import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { first, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';

import { SceneService } from '../../services/scene.service';
import { TutorialStorageService } from '../../services/tutorial-storage.service';

import { SceneComponent } from './scene/scene.component';
import { GameResultDialogComponent, GameResultDialogData } from './game-result-dialog/game-result-dialog.component';
import { WelcomingDialogComponent } from './welcoming-dialog/welcoming-dialog.component';
import { TutorialOverlayComponent } from './tutorial-overlay/tutorial-overlay.component';
import { LoadingOverlayComponent } from './loading-overlay/loading-overlay.component';
import { GameResetDialogComponent } from './game-reset-dialog/game-reset-dialog.component';

/** Game component. */
@Component({
	selector: 'gaw-game',
	templateUrl: './game.component.html',
	styleUrls: ['./game.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		SceneComponent,
		LoadingOverlayComponent,
		TutorialOverlayComponent,
		MatIcon,
	],
})
export class GameComponent implements OnInit {
	private readonly dialog = inject(MatDialog);

	private readonly sceneService = inject(SceneService);

	private readonly tutorialStorage = inject(TutorialStorageService);

	private readonly destroyRef = inject(DestroyRef);

	/** Whether scene is loading or not. */
	protected readonly isLoading = toSignal(this.sceneService.isLoading$);

	/** Whether controls are available or not. */
	protected readonly areControlsAvailable = computed(() =>
		this.isTutorialShown() &&
		!this.isLoading());

	private readonly isTutorialShown = this.tutorialStorage.isShown;

	/** @inheritdoc */
	public constructor() {
		this.openWelcomingDialog();
	}

	/** @inheritdoc */
	public ngOnInit(): void {
		this.sceneService.endGameStrokes$.pipe(
			tap(strokesCount => this.openGameResultDialog(strokesCount)),
			takeUntilDestroyed(this.destroyRef),
		).subscribe();
	}

	/** Handles reset game button click. */
	protected onResetGame(): void {
		this.dialog.open<GameResetDialogComponent>(GameResetDialogComponent);
	}

	/** Handles open tutorial button click. */
	protected onOpenTutorial(): void {
		this.tutorialStorage.removeIsShown()
			.pipe(first())
			.subscribe();
	}

	private openWelcomingDialog(): void {
		this.dialog.open(WelcomingDialogComponent, {
			position: { left: '0' },
		});
	}

	private openGameResultDialog(strokesCount: number): void {
		this.dialog.open<GameResultDialogComponent, GameResultDialogData>(GameResultDialogComponent, {
			disableClose: true,
			data: { strokesCount },
		});
	}
}
